'use client'

import { usePathname } from 'next/navigation'
import { Suspense } from 'react'

import { PortableTextBlock } from 'next-sanity'

import {
  DemoRequestForm,
  DemoRequestFormFromUrl,
} from '@/app/components/DemoRequest/DemoRequestForm'
import PortableTextMinimal from '@/app/components/PortableTextMinimal'
import { TRACKING_LOCATIONS, TrackingLocation } from '@/app/utils/analytics'
import { REQUEST_DEMO_PATH } from '@/app/utils/routes'
import { cn } from '@/app/utils/style'
import { DemoForm as DemoFormRaw } from '@/sanity.types'
import { dataAttr } from '@/sanity/lib/utils'

export interface DemoRequestProps {
  demoForm: DemoFormRaw & { _key?: string }
  displayMode?: 'page' | 'modal'
  className?: string
  documentId?: string
  documentType?: string
  parentPath?: string
}

export const DemoRequest = ({
  demoForm,
  displayMode = 'page',
  className,
  documentId,
  documentType,
  parentPath,
}: DemoRequestProps) => {
  const pathname = usePathname()
  const isDedicatedDemoPage = pathname === REQUEST_DEMO_PATH
  // Only the dedicated request-demo page reads ?user= to preselect the account type
  const shouldUseUrlPrefill = displayMode === 'page' && isDedicatedDemoPage

  const tracking: TrackingLocation = {
    location:
      displayMode === 'modal'
        ? TRACKING_LOCATIONS.MODAL
        : isDedicatedDemoPage
          ? TRACKING_LOCATIONS.LANDING_PAGE
          : TRACKING_LOCATIONS.PAGE_CONTENT,
  }

  const attr =
    demoForm._key && documentId && documentType && parentPath
      ? dataAttr({
          id: documentId,
          type: documentType,
          path: `${parentPath}[_key=="${demoForm._key}"]`,
        })
      : undefined

  return (
    <div
      className={cn('w-full flex flex-col lg:grid', className)}
      style={{
        gridTemplateColumns:
          displayMode === 'modal'
            ? 'minmax(auto, 460px) minmax(460px, 1fr)'
            : 'minmax(auto, 860px) minmax(556px, 1fr)',
      }}
      {...(displayMode === 'page' && { 'data-demo-form-page': '' })}
      {...(attr && { 'data-sanity': attr.toString() })}
    >
      <div
        className={cn(
          'relative w-full bg-cover bg-center md:flex md:justify-end',
          'bg-[url(/images_pwf/demo_bg_mobile.jpg)] md:bg-[url(/images_pwf/demo_bg_desktop.jpg)]',
        )}
      >
        <div className="w-full p-8xl flex flex-col gap-sm md:w-auto md:flex-grow md:justify-end md:items-start">
          <div
            className={cn(
              'size-header-h5 font-medium text-white  lg:font-normal',
              displayMode === 'page' ? 'lg:size-header-h3' : 'lg:size-header-h4',
            )}
          >
            <PortableTextMinimal
              value={
                (demoForm.heading as PortableTextBlock[]) || [
                  {
                    _type: 'block',
                    children: [{ _type: 'span', text: 'Unlock smarter newsletter advertising' }],
                  },
                ]
              }
              tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
            />
          </div>
          <div className="size-body-sm text-white [&_a]:text-green-100 lg:size-body-md">
            <PortableTextMinimal
              value={
                (demoForm.subheading as PortableTextBlock[]) || [
                  {
                    _type: 'block',
                    children: [
                      { _type: 'span', text: 'Want to see how Paved can help grow your business?' },
                    ],
                  },
                ]
              }
              tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
              enableMailtoLinks
            />
          </div>
        </div>
      </div>
      <div
        className={cn(
          'bg-white md:flex md:flex-col md:justify-center',
          displayMode === 'page' && 'md:min-h-screen',
        )}
      >
        <div className="w-full py-5xl px-8xl">
          {shouldUseUrlPrefill ? (
            <Suspense fallback={null}>
              <DemoRequestFormFromUrl tracking={tracking} displayMode={displayMode} />
            </Suspense>
          ) : (
            <DemoRequestForm tracking={tracking} displayMode={displayMode} />
          )}
        </div>
      </div>
    </div>
  )
}
